import React, { useState } from 'react';
import { X, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const ReviewFormModal = ({ isOpen, onClose, onSubmitted }) => {
  const { t, i18n } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const reset = () => {
    setRating(0); setHover(0); setName(''); setCompany(''); setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error(t('reviews.needRating', 'Please select a rating')); return; }
    if (!name.trim() || !message.trim()) { toast.error(t('reviews.needFields', 'Please fill in your name and review')); return; }
    setSubmitting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), company: company.trim() || null, rating, message: message.trim(), locale: i18n.language })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(t('reviews.thankYou', 'Thank you! Your review will be published after moderation.'));
        reset();
        onClose();
        onSubmitted && onSubmitted();
      } else {
        toast.error(data.detail || t('reviews.error', 'Something went wrong. Please try again.'));
      }
    } catch (err) {
      toast.error(t('reviews.error', 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50" data-testid="review-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 md:p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" aria-label="Close" data-testid="review-modal-close">
          <X size={24} />
        </button>
        <h3 className="text-2xl font-bold text-[#0A0A0A] mb-1">{t('reviews.formTitle', 'Leave a Review')}</h3>
        <p className="text-gray-600 text-sm mb-6">{t('reviews.formSubtitle', 'Share your experience working with Digital Corp.')}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('reviews.yourRating', 'Your rating')}</label>
            <div className="flex gap-1" data-testid="review-stars-input">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setRating(s)}
                  onMouseEnter={() => setHover(s)}
                  onMouseLeave={() => setHover(0)}
                  className="p-1"
                  data-testid={`review-star-${s}`}
                  aria-label={`${s} stars`}
                >
                  <Star size={32} className={(hover || rating) >= s ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
                </button>
              ))}
            </div>
          </div>
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder={t('reviews.namePlaceholder', 'Your name *')}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B3F84]"
            required data-testid="review-name"
          />
          <input
            type="text" value={company} onChange={(e) => setCompany(e.target.value)}
            placeholder={t('reviews.companyPlaceholder', 'Company / role (optional)')}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B3F84]"
            data-testid="review-company"
          />
          <textarea
            value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
            placeholder={t('reviews.messagePlaceholder', 'Your review *')}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B3F84]"
            required data-testid="review-message"
          />
          <button
            type="submit" disabled={submitting}
            className="w-full px-6 py-3 bg-[#0B3F84] text-white font-semibold rounded-lg hover:bg-[#0a3570] disabled:opacity-50"
            data-testid="review-submit"
          >
            {submitting ? '...' : t('reviews.submit', 'Submit Review')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReviewFormModal;
